import { useEffect, useRef } from 'react'

import { useEchoClient } from './echoClient'

import { findRoomById, selectRealtimeRoomIdsKey, useChatStore } from '@/stores/chatStore'

import { useAuthStore } from '@/stores/authStore'

import { playChatSound } from '@/utils/chatSound'

import type { ChatMessage, ChatReactionAggregate } from '@/api/chat'

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo chat]', ...args)
  }
}

interface MessagePayload {
  message: ChatMessage
}

interface ReadMarkPayload {
  chat_room_id: number
  user_id: number
  last_read_message_id: number
}

interface ReactionPayload {
  chat_room_id: number
  message_id: number
  reactions: ChatReactionAggregate[]
  reactions_version?: number
}

/**
 * Подписка на private-каналы chat.{roomId} для всех комнат пользователя,
 * включая открытые комнаты уроков (в списке диалогов их нет).
 * События: chat.message.created|updated|deleted, chat.room.read_mark_updated.
 *
 * Подписки ведутся по дельте: набор комнат меняется на каждом входе на урок и
 * выходе с него, а полная переподписка оставляла бы окно, в котором события
 * остальных чатов уже не приходят и восполнить их нечем.
 */
export function useChatRealtime() {
  const roomIdsKey = useChatStore(selectRealtimeRoomIdsKey)
  const currentUserId = useAuthStore((s) => s.user?.id ?? 0)

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  const subscribedRef = useRef<Set<number>>(new Set())
  // Обработчики читают актуального пользователя из ref: смена аккаунта не
  // должна переподписывать каналы.
  const currentUserIdRef = useRef(currentUserId)
  currentUserIdRef.current = currentUserId

  useEffect(() => {
    const subscribed = subscribedRef.current
    const store = useChatStore.getState

    const leave = (roomId: number) => {
      try {
        echo.leave(`chat.${roomId}`)
      } catch {
        // ignore
      }
      subscribed.delete(roomId)
    }

    const desired = new Set(
      roomIdsKey === '' ? [] : roomIdsKey.split(',').map((id) => Number(id))
    )

    for (const roomId of [...subscribed]) {
      if (!desired.has(roomId)) leave(roomId)
    }

    for (const roomId of desired) {
      if (subscribed.has(roomId)) continue

      const channelName = `chat.${roomId}`
      echo
        .private(channelName)
        .listen('.chat.message.created', (payload: MessagePayload) => {
          const message = payload.message
          store().applyIncomingMessage(message)

          // Комнату ищем и среди уроков: иначе звук и mute работали бы
          // только в обычных чатах, хотя подписка у них общая.
          const room = findRoomById(store(), message.chat_room_id)
          if (
            message.author_user_id !== currentUserIdRef.current &&
            room &&
            !room.is_muted &&
            store().currentRoomId !== message.chat_room_id
          ) {
            playChatSound()
          }
        })
        .listen('.chat.message.updated', (payload: MessagePayload) => {
          store().applyUpdatedMessage(payload.message)
        })
        .listen('.chat.message.deleted', (payload: MessagePayload) => {
          store().applyDeletedMessage(payload.message)
        })
        .listen('.chat.room.read_mark_updated', (payload: ReadMarkPayload) => {
          store().applyReadMark(
            payload.chat_room_id,
            payload.user_id,
            payload.last_read_message_id
          )
        })
        .listen('.chat.message.reaction_updated', (payload: ReactionPayload) => {
          store().applyRealtimeReactionUpdate(
            payload.chat_room_id,
            payload.message_id,
            payload.reactions,
            payload.reactions_version
          )
        })

      subscribed.add(roomId)
      echoDevLog(`Подписка на private-${channelName}`)
    }
  }, [roomIdsKey, echo])

  // Полная отписка — только при пересоздании клиента и размонтировании: на
  // смену набора комнат каналы не трогаются, этим занимается дельта выше.
  useEffect(() => {
    const subscribed = subscribedRef.current

    return () => {
      for (const roomId of [...subscribed]) {
        try {
          echo.leave(`chat.${roomId}`)
        } catch {
          // ignore
        }
        subscribed.delete(roomId)
      }
    }
  }, [echo])
}
